<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import FactEditor from './FactEditor.vue';
import { useAlert } from 'dashboard/composables';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { debounce } from '@chatwoot/utils';

const props = defineProps({
  assistantId: {
    type: Number,
    default: null,
  },
});

const store = useStore();

const selectedLibraries = ref([]);
const isLibraryListUpdating = ref(false);

const v$ = useVuelidate(
  {
    selectedLibraries: {
      required,
    },
  },
  { selectedLibraries }
);

const libraries = useMapGetter('knowledgebase/getLibraries');
const facts = useMapGetter('facts/getFacts');

const getKnowledgebaseDisplayName = library => {
  const typeEmojiMap = {
    documents: '📄 ',
    catalog: '🗂️ ',
    gallery: '🖼️ ',
  };

  const emoji = typeEmojiMap[library.type] || '📁';
  return `${emoji} ${library.name}`;
};

const librariesWithEmoji = computed(() => {
  return libraries.value.map(kb => ({
    ...kb,
    displayName: getKnowledgebaseDisplayName(kb),
  }));
});

const activeAssistant = useFunctionGetter(
  'assistants/getAssistant',
  props.assistantId
);

const fetchLibraries = () => {
  store.dispatch('knowledgebase/get');
};

const fetchFacts = () => {
  store.dispatch('facts/fetchFacts', activeAssistant.value.id);
};

watch(librariesWithEmoji, () => {
  if (!activeAssistant.value) return;

  selectedLibraries.value = librariesWithEmoji.value.filter(lib =>
    activeAssistant.value.libraries.some(k => {
      return String(k.id) === String(lib.id);
    })
  );
});

const setIsKnowledgebaseListUpdatingFalse = debounce(
  () => {
    isLibraryListUpdating.value = false;
  },
  400,
  false
);

const updateAssistant = async () => {
  isLibraryListUpdating.value = true;
  try {
    // Send only libraries — do not spread the full assistant. Spreading includes
    // procedures from Vuex (often without attributes); the API would replace
    // server procedures with that incomplete payload and drop attributes.
    await store.dispatch('assistants/update', {
      id: activeAssistant.value.id,
      libraries: selectedLibraries.value.map(kb => {
        return {
          id: kb.id,
        };
      }),
    });
    setIsKnowledgebaseListUpdatingFalse();
  } catch (e) {
    useAlert('ASSISTANT_MGMT.KNOWLEDGE.ERROR');
    setIsKnowledgebaseListUpdatingFalse();
  }
};

const createNewFact = async () => {
  const newFact = { content: '' };
  await store.dispatch('facts/createFact', {
    rookooAgentId: activeAssistant.value.id,
    fact: newFact,
  });
  fetchFacts();
};

const removeFact = async fact => {
  await store.dispatch('facts/deleteFact', {
    rookooAgentId: activeAssistant.value.id,
    factKey: fact.key,
  });
  fetchFacts();
};

onMounted(() => {
  fetchLibraries();
  fetchFacts();
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('ASSISTANT_MGMT.KNOWLEDGE.ASSISTANT_LIBRARIES')"
      :sub-title="$t('ASSISTANT_MGMT.KNOWLEDGE.ASSISTANT_LIBRARIES_SUB_TEXT')"
    >
      <multiselect
        v-model="selectedLibraries"
        :options="librariesWithEmoji"
        track-by="id"
        label="displayName"
        multiple
        :close-on-select="false"
        :clear-on-select="false"
        hide-selected
        placeholder="Pick some"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        @select="v$.selectedLibraries.$touch"
      />

      <NextButton
        :label="$t('ASSISTANT_MGMT.KNOWLEDGE.UPDATE')"
        :is-loading="isLibraryListUpdating"
        @click="updateAssistant"
      />
    </SettingsSection>

    <SettingsSection
      :title="$t('ASSISTANT_MGMT.KNOWLEDGE.ASSISTANT_FACTS')"
      :sub-title="$t('ASSISTANT_MGMT.KNOWLEDGE.ASSISTANT_FACTS_SUB_TEXT')"
    >
      <div
        v-for="(fact, index) in facts"
        :key="`fact-${fact.key}`"
        class="mb-4"
      >
        <FactEditor
          :assistant="activeAssistant"
          :fact-key="fact.key"
          :index="index"
          @remove="removeFact"
        />
      </div>
      <NextButton
        :label="$t('ASSISTANT_MGMT.KNOWLEDGE.ASSISTANT_FACTS_NEW')"
        @click="createNewFact"
      />
    </SettingsSection>
  </div>
</template>

<style scoped lang="scss">
/* Component styles */
</style>
